import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { Alert, Badge, Button, Card, Input, Select } from '../../components/ui';

const BASE = '/api/v1/superadmin/kb/kr-mfds';
const FAMILIES = [
  'hff_act',
  'hff_code',
  'hff_label',
  'food_label',
  'claims',
  'food_materials',
  'customs_import',
];
interface Proposal {
  id: string;
  title: string;
  url: string;
  family: string;
  citation: string;
  status: string;
  publishStage?: string;
  publishError?: string | null;
  publishedVersion?: string;
  reviewDraft?: { status: string; updatedAt: string; blockers?: string[]; effectiveDate?: string };
}
interface ReviewDraft {
  status: string;
  summary: string;
  blockers: string[];
  reviewedText: string;
  effectiveDate: string;
  rules: unknown[];
  reviewSourceUrl: string;
  reviewSourceHash: string;
  sourceLanguage: string;
}
interface State {
  supplementarySources?: { title: string; url: string; scope: string; active: boolean }[];
  ready: boolean;
  reason: string | null;
  manifest: {
    version: string;
    folderId: string;
    files: { title: string; family: string; url: string }[];
    rules: unknown[];
  } | null;
  updates: Proposal[];
  refresh: { status?: string; error?: string; found?: number; sources?: number } | null;
  publishing: { holder?: string | null; error?: string } | null;
}
export function MfdsKbPanel() {
  const { t } = useTranslation();
  const [st, set] = useState<State | null>(null),
    [err, setErr] = useState(''),
    [busy, setBusy] = useState(false);
  const [source, setSource] = useState({ title: '', url: '', family: 'hff_act', citation: '' });
  const [editing, setEditing] = useState<Proposal | null>(null),
    [text, setText] = useState(''),
    [rules, setRules] = useState('[]');
  const [effective, setEffective] = useState(''),
    [attest, setAttest] = useState(false);
  const [draft, setDraft] = useState<ReviewDraft | null>(null);
  const loadedDraft = useRef('');
  const load = useCallback(async () => {
    try {
      set(await api<State>(BASE));
    } catch (e) {
      setErr(String(e));
    }
  }, []);
  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 5000);
    return () => clearInterval(timer);
  }, [load]);
  const draftMeta = st?.updates.find((p) => p.id === editing?.id)?.reviewDraft;
  useEffect(() => {
    if (!editing || !draftMeta || !['ready', 'blocked', 'failed'].includes(draftMeta.status)) return;
    const key = editing.id + draftMeta.updatedAt;
    if (loadedDraft.current === key) return;
    let cancelled = false;
    void api<ReviewDraft>(`${BASE}/updates/${editing.id}/suggestion`)
      .then((d) => {
        if (cancelled) return;
        loadedDraft.current = key;
        setDraft(d);
        setText(d.reviewedText);
        setRules(JSON.stringify(d.rules, null, 2));
        setEffective(d.effectiveDate);
        setAttest(d.status === 'ready');
      })
      .catch((e) => {
        if (!cancelled) setErr(String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [editing, draftMeta]);
  const act = async (path: string, body: unknown) => {
    setErr('');
    setBusy(true);
    try {
      await api(`${BASE}${path}`, { method: 'POST', json: body });
      await load();
      return true;
    } catch (e) {
      setErr((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4">
      <Card title="MFDS(KR)">
        <div className="flex flex-wrap gap-3 items-center">
          <Badge tone={st?.ready ? 'green' : 'amber'}>
            {st?.ready ? t('markets.ready') : t('markets.notReady')}
          </Badge>
          <span className="font-mono text-xs">{st?.manifest?.version ?? '-'}</span>
          <Button
            disabled={busy || ['queued', 'running'].includes(st?.refresh?.status ?? '')}
            onClick={() => void act('/refresh', {})}
          >
            {t('sakb.refresh.check')}
          </Button>
        </div>
        <p className="mt-2 text-sm">{t('markets.mfdsExplanation')}</p>
        {st?.reason && <Alert tone="amber">{st.reason}</Alert>}
        {st?.refresh && (
          <p className="text-xs mt-2">
            {st.refresh.status} · {st.refresh.found ?? 0} sources · {st.refresh.sources ?? 0} grounded sources{' '}
            {st.refresh.error}
          </p>
        )}
        {err && <Alert>{err}</Alert>}
        {st?.publishing?.error && <Alert>{st.publishing.error}</Alert>}
        {st?.publishing?.holder && <p className="text-sm">{t('sakb.refresh.publishing')}</p>}
      </Card>
      {!!st?.supplementarySources?.length && (
        <Card title={t('markets.references')}>
          {st.supplementarySources.map((s) => (
            <div key={s.url} className="space-y-1 py-2 border-b text-sm">
              <Badge tone={s.active ? 'green' : 'amber'}>
                {s.active ? t('markets.referenceActive') : t('markets.referencePending')}
              </Badge>{' '}
              {s.title}
              <a className="block text-emerald-700 underline" href={s.url} target="_blank" rel="noreferrer">
                {s.url}
              </a>
              <p className="text-xs text-slate-500">{s.scope}</p>
            </div>
          ))}
        </Card>
      )}
      <Card title={t('markets.addSource')}>
        <div className="grid gap-2 sm:grid-cols-2">
          <Input
            placeholder={t('markets.sourceTitle')}
            value={source.title}
            onChange={(e) => setSource({ ...source, title: e.target.value })}
          />
          <Input
            placeholder="https://www.mfds.go.kr/..."
            value={source.url}
            onChange={(e) => setSource({ ...source, url: e.target.value })}
          />
          <Select value={source.family} onChange={(e) => setSource({ ...source, family: e.target.value })}>
            {FAMILIES.map((f) => (
              <option key={f}>{f}</option>
            ))}
          </Select>
          <Input
            placeholder={t('sakb.citation')}
            value={source.citation}
            onChange={(e) => setSource({ ...source, citation: e.target.value })}
          />
        </div>
        <Button
          className="mt-2"
          disabled={busy || !source.title || !source.url}
          onClick={() => void act('/updates', source)}
        >
          {t('markets.addSource')}
        </Button>
      </Card>
      <Card title={t('markets.proposals')}>
        {(st?.updates ?? []).map((p) => (
          <div key={p.id} className="border-b py-3 space-y-2">
            <p>
              <Badge>{p.family}</Badge> {p.title} · {p.status}
            </p>
            {p.status === 'publishing' && (
              <Alert tone="amber">
                {t('markets.publicationProgress')}:{' '}
                {t(`markets.stage_${p.publishStage ?? 'queued'}`, {
                  defaultValue: p.publishStage ?? 'queued',
                })}
              </Alert>
            )}
            {p.publishError && <Alert>{p.publishError}</Alert>}
            {p.status === 'accepted' && (
              <Alert tone="green">
                {t('markets.publicationComplete')} · {p.publishedVersion}
              </Alert>
            )}
            <a
              href={p.url.startsWith('https://') ? p.url : undefined}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-emerald-700 underline"
            >
              {p.url}
            </a>
            {p.status === 'pending_review' && (
              <div className="flex gap-2">
                <Button
                  disabled={busy}
                  onClick={() => {
                    setEditing(p);
                    setText('');
                    setRules('[]');
                    setEffective('');
                    setAttest(false);
                    setDraft(null);
                    loadedDraft.current = '';
                    void act(`/updates/${p.id}/suggest-review`, {});
                  }}
                >
                  {t('markets.bestReview')}
                </Button>
                <Button
                  variant="ghost"
                  disabled={busy}
                  onClick={() => void act(`/updates/${p.id}/dismiss`, {})}
                >
                  {t('sakb.refresh.dismiss')}
                </Button>
              </div>
            )}
            {editing?.id === p.id && (
              <div className="space-y-2 rounded border p-3">
                {!draft && <p>{t('markets.preparingReview')}</p>}
                {draft && (
                  <div className="space-y-2">
                    <Badge tone={draft.status === 'ready' ? 'green' : 'amber'}>
                      {draft.status === 'ready' ? t('markets.defaultsReady') : t('markets.defaultsBlocked')}
                    </Badge>
                    <p>{draft.summary}</p>
                    {draft.blockers.map((b, i) => (
                      <Alert tone="amber" key={i}>
                        {b}
                      </Alert>
                    ))}
                    {draft.reviewSourceUrl && (
                      <a
                        href={draft.reviewSourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-emerald-700 underline"
                      >
                        {t('sakb.refresh.source')}
                      </a>
                    )}
                    <Button
                      variant="ghost"
                      disabled={busy}
                      onClick={() => {
                        setDraft(null);
                        loadedDraft.current = '';
                        void act(`/updates/${p.id}/suggest-review`, { force: true });
                      }}
                    >
                      {t('markets.regenerateReview')}
                    </Button>
                  </div>
                )}
                <p className="text-xs">{t('markets.primaryReview')}</p>
                <details>
                  <summary className="cursor-pointer">{t('markets.reviewDetails')}</summary>
                  <label className="block">
                    {t('markets.effectiveDate')}
                    <Input
                      type="date"
                      value={effective}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setEffective(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    {t('markets.reviewedText')}
                    <textarea
                      className="block w-full rounded border p-2"
                      rows={8}
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    {t('markets.rulesJson')}
                    <textarea
                      className="block w-full rounded border p-2 font-mono text-xs"
                      rows={6}
                      value={rules}
                      onChange={(e) => setRules(e.target.value)}
                    />
                  </label>
                  <p className="text-xs text-slate-600">{t('markets.ruleFormat')}</p>
                  <code className="block whitespace-pre-wrap text-xs">
                    {
                      '[{"id":"rule-id","kind":"required_text|forbidden_claim","context":"label|advertising|all","phrases":["..."],"section":"...","sourceQuote":"...","severity":"major","finding":"...","fix":"..."}]'
                    }
                  </code>
                  <label className="flex gap-2">
                    <input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} />
                    {t('markets.attestPrimary')}
                  </label>
                </details>
                <Button
                  disabled={busy || draft?.status !== 'ready' || !attest || text.length < 200 || !effective}
                  onClick={() =>
                    void (async () => {
                      try {
                        const accepted = await act(`/updates/${p.id}/accept`, {
                          reviewedText: text,
                          ...(draft &&
                          text === draft.reviewedText &&
                          effective === draft.effectiveDate &&
                          rules === JSON.stringify(draft.rules, null, 2)
                            ? { useSuggestion: true }
                            : {}),
                          effectiveDate: effective,
                          primaryKorean: draft?.sourceLanguage !== 'en',
                          annexesReviewed: true,
                          finalAndApplicable: true,
                          rules: JSON.parse(rules),
                          ...(draft?.reviewSourceHash
                            ? {
                                reviewSourceUrl: draft.reviewSourceUrl,
                                reviewSourceHash: draft.reviewSourceHash,
                              }
                            : {}),
                        });
                        if (accepted) setEditing(null);
                      } catch (e) {
                        setErr(String(e));
                      }
                    })()
                  }
                >
                  {t('markets.publishDefaults')}
                </Button>
              </div>
            )}
          </div>
        ))}
      </Card>
      {st?.manifest && (
        <Card title={t('markets.activeSources')}>
          {st.manifest.files.map((f) => (
            <p key={f.url} className="text-sm">
              <Badge>{f.family}</Badge> {f.title}
            </p>
          ))}
          <p className="text-xs">
            {st.manifest.rules.length} reviewed rules · VaultSage folder {st.manifest.folderId}
          </p>
        </Card>
      )}
    </div>
  );
}
